import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useGtm } from 'vue-gtm'
import { storeToRefs } from 'pinia'
import { useValidate } from '@wiosna-dev/wiosna-ui'

import { PAYMENT_DATALAYER } from '@/constants/payment'
import type { Maybe, PaymentInfoForGtm, PaymentInfoForDataLayer } from '@/types'
import { paymentInfoForDataLayerSchema } from '@/schemas/payment'
import useTransactionDataStore from '@/stores/transactionData'

const useDataLayerStore = defineStore(
  'dataLayer',
  () => {
    const transactionInfoForGtm = ref<Maybe<PaymentInfoForDataLayer>>(null)

    const saveDataForDataLayer = (
      orderId: number | null = null,
      installmentID: number | null = null
    ): void => {
      const transactionDataStore = useTransactionDataStore()
      const { paymentType, getSelectedOrOtherAmount: amount } = storeToRefs(transactionDataStore)

      transactionInfoForGtm.value = {
        transactionId: orderId,
        amount: amount.value,
        typeOfSupport: paymentType.value,
        installmentID: installmentID ?? 0
      }
    }

    const getPaymentInfoForGtm = (): PaymentInfoForGtm | undefined => {
      useValidate(transactionInfoForGtm.value, paymentInfoForDataLayerSchema)
      if (!transactionInfoForGtm.value) return

      const paymentDataLayer = PAYMENT_DATALAYER[transactionInfoForGtm.value.typeOfSupport]

      const { amount, installmentID } = transactionInfoForGtm.value
      const quantity = 1

      const paymentInfoForGtm: PaymentInfoForGtm = {
        event: 'indeks_podziekowanie',
        transactionId: transactionInfoForGtm.value.transactionId,
        transactionAffiliation: 'Stowarzyszenie Wiosna',
        transactionTotal: amount ?? 0,
        transactionTax: 0,
        transactionShipping: 0,
        transactionProducts: [
          {
            sku: paymentDataLayer.sku,
            name: paymentDataLayer.name,
            category: 'AP',
            price: amount ?? 0,
            quantity,
            installmentID: installmentID ?? 0
          }
        ]
      }
      transactionInfoForGtm.value = null
      return paymentInfoForGtm
    }

    const sendInfoToGtm = (): void => {
      const gtm = useGtm()
      const paymentInfoForGtm = getPaymentInfoForGtm()
      if (paymentInfoForGtm && gtm) {
        gtm.trackEvent(paymentInfoForGtm)
      }
    }

    return {
      transactionInfoForGtm,
      saveDataForDataLayer,
      getPaymentInfoForGtm,
      sendInfoToGtm
    }
  },
  {
    persist: {
      storage: sessionStorage,
      pick: ['transactionInfoForGtm']
    }
  }
)

export default useDataLayerStore
